import React, {useContext, useMemo, useState} from 'react'
import _ from 'lodash'
import classNames from 'classnames'
import dayjs from 'dayjs'
import {Modal} from 'antd'
import {BsEyeFill, BsGlobeAmericas, BsMegaphoneFill, BsPeopleFill, BsSendFill,} from 'react-icons/bs'
import {FaChalkboard} from 'react-icons/fa'
import useTranslate from 'modules/local/useTranslate'
import useAsync from 'modules/asyncCache/useAsync'
import {getResponseItems} from 'views/Shared'
import {bindQueryParams} from 'views/AppLocals/functions/routerHelper'
import {announcement_gets_Api, announcement_getsByClassesOwner_Api, announcement_getsByCoursesOwner_Api} from 'apis'
import WorkspaceContext from '../../WorkspaceContext'
import {useAnnouncementForm} from 'views/AppLocals/Announcement/useAnnouncementForm'
import CRUD from 'views/Shared/enums/CRUD'
import {CourseClassSelector} from 'views/AppLocals/Course/components/CourseClassSelector'
import {CourseSelector} from 'views/AppLocals/Course/components/CourseSelector'

// "Quản lý Thông báo Hệ thống & Lớp học" - khớp mockup bạn gửi. Dữ liệu THẬT lấy từ hệ thống
// Announcement chung (xem docs/architecture/announcements-timetables.md, kristo repo) - 3 owner_type
// áp dụng ở dashboard này: 'organization' (chung toàn trung tâm), 'course' (cả khóa - MỌI lớp trong 1
// course) và 'course_class' (riêng 1 lớp) - đúng 3 nhóm tab "Thông báo chung"/"Theo khóa"/"Theo lớp cụ
// thể". Gộp từ 3 API:
//   - announcement_gets_Api (owner_type=organization) - trả CẢ announcement toàn tổ chức LẪN
//     announcement riêng từng group/department con (khi không truyền group_id) - lọc lại CLIENT-SIDE
//     chỉ giữ owner_type==='organization' (loại bỏ announcement của group khác, vd ca đoàn/hội đoàn -
//     không thuộc phạm vi "Đào tạo & Huấn luyện").
//   - announcement_getsByCoursesOwner_Api (type=organization) - announcement của MỌI course (owner_type
//     ='course') thuộc org này.
//   - announcement_getsByClassesOwner_Api (type=organization) - announcement của MỌI course_class
//     thuộc org này.
//
// "Đã đọc" xấp xỉ bằng total_views (không có bảng đọc/chưa-đọc PER STUDENT expose qua API list) -
// mẫu số: course_class dùng course_class.student_count, course dùng course.student_count (cả 2 đều
// có sẵn trong response), organization dùng tổng student_count của cả org (truyền qua prop
// orgStudentCount, từ workspace_settings.courses.statistics đã có ở CourseDashboard.js) - CHỈ LÀ XẤP
// XỈ, không phải tỉ lệ đọc thật per-user.
//
// "Người phát" không có field poster/user lộ ra qua AnnouncementTransformerBasic - course_class
// dùng tên giáo viên phụ trách (teacher) làm xấp xỉ hợp lý, course/organization không có field tương
// đương nên fallback tên khóa/tổ chức.
//
// "Tạo & Phát Thông Báo" - đã nối cả 3 owner_type thật sự áp dụng ở dashboard này (khớp đúng 3 tab lọc
// đã có sẵn) - owner_type='group' KHÔNG áp dụng ở đây vì nó nghĩa là 1 group ĐỘC LẬP không thuộc org
// nào (vd ca đoàn riêng) - xem comment trong useAnnouncementForm.js - trong khi Course/Lớp học LUÔN
// nằm trong 1 organization, không có khái niệm "lớp độc lập".
//   - Tab "Thông báo chung" (owner_type='organization') - CHỦ ĐỘNG chọn đúng tab này mới tạo thẳng
//     form organization (useAnnouncementForm({owner_type:'organization', ownerItem:organization, groups}) -
//     đúng hook đang dùng ở WorkspaceOrganizationAnnouncements.js, không tự chế modal/API riêng).
//   - Tab "Theo khóa" (owner_type='course') - bấm nút mở 1 bước chọn KHÓA trước (coursePickerModal,
//     dùng CourseSelector chế độ toàn-org qua owner_id/owner_type), chọn xong mới mở form thật với
//     useAnnouncementForm({owner_type:'course', ownerItem: pickedCourseForAnnouncement}).
//   - Tab "Tất cả" (landing mặc định) VÀ tab "Theo lớp cụ thể" (owner_type='course_class') - bấm nút
//     mở 1 bước chọn lớp nhỏ trước (classPickerModal, dùng CourseClassSelector chế độ toàn-org qua
//     owner_id/owner_type - xem comment trong CourseClassSelector.js), chọn xong mới mở form thật với
//     useAnnouncementForm({owner_type:'course_class', ownerItem: pickedClassForAnnouncement}).
//     Mặc định rơi vào course_class (thay vì organization/course) vì nhu cầu thực tế phổ biến hơn ở
//     khối này là phát thông báo TRONG 1 lớp học cụ thể, không phải toàn trung tâm hay cả khóa.
// refresh_token của CẢ 3 hook -> thêm vào deps 3 useAsync bên dưới để danh sách tự tải lại ngay sau
// khi tạo xong (dù tạo ở scope nào), không cần bấm refresh tay.

const TYPE_STYLE = {
    organization: {bg: '#fff7ed', text: '#c2410c', icon: <BsGlobeAmericas size={11}/>},
    course: {bg: '#ecfdf5', text: '#047857', icon: <FaChalkboard size={11}/>},
    course_class: {bg: '#eef2ff', text: '#4338ca', icon: <BsPeopleFill size={11}/>},
}

const FILTER_TABS = [
    {key: 'all', label: 'all', ownerType: null},
    {key: 'general', label: 'general notifications', ownerType: 'organization'},
    {key: 'course', label: 'by whole course', ownerType: 'course'},
    {key: 'class', label: 'by specific class', ownerType: 'course_class'},
]

const renderTypeBadge = (item, t) => {
    const cfg = TYPE_STYLE[item.owner_type] || TYPE_STYLE.organization
    const label = item.owner_type === 'course_class'
        ? `${t('class')}: ${item.course_class?.title || ''}`
        : item.owner_type === 'course'
            ? `${t('course')}: ${item.course?.title || ''}`
            : t('general notification for whole center')
    return (
        <span
            style={{background: cfg.bg, color: cfg.text}}
            className="px-2.5 py-1 flex items-center gap-1.5 rounded-full text-2xs font-bold uppercase tracking-wide whitespace-nowrap">
            {cfg.icon}
            {label}
        </span>
    )
}

const NotificationRow = ({item, orgTitle, orgStudentCount, t}) => {
    const totalCount = item.owner_type === 'course_class'
        ? (item.course_class?.student_count ?? 0)
        : item.owner_type === 'course'
            ? (item.course?.student_count ?? 0)
            : (orgStudentCount ?? 0)
    const readCount = Math.min(Math.round(item.total_views ?? 0), totalCount || Infinity)
    const pct = totalCount > 0 ? Math.round((readCount / totalCount) * 100) : 0
    const publisher = item.owner_type === 'course_class'
        ? (item.teacher?.name || item.course_class?.title || orgTitle)
        : item.owner_type === 'course'
            ? (item.course?.title || orgTitle)
            : orgTitle

    return (
        <div className="py-4 md:py-5 first:pt-0 last:pb-0 flex flex-col gap-2">
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="flex flex-wrap items-center gap-1.5">
                    {renderTypeBadge(item, t)}
                    {!!item.type_label && (
                        <span className="px-2 py-1 rounded-full text-2xs font-bold whitespace-nowrap bg-slate-100 text-slate-500">
                            {item.type_label}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-3 text-xs shrink-0">
                    <span className="text-slate-400 whitespace-nowrap">
                        {t('posted at')}: {item.created ? dayjs(item.created).format('DD/MM · HH:mm') : ''}
                    </span>
                    {/* Nút Chỉnh sửa/Xóa TRƯỚC ĐÂY hiện sẵn nhưng không gắn onClick nào - bấm vào
                        không có tác dụng gì (bạn báo lỗi này, đúng ý ghi chú đầu file: nút "Tạo &
                        Phát Thông Báo" cũng CHƯA nối form thật). Bỏ hẳn 2 nút này cho tới khi có luồng
                        sửa/xóa thông báo thật (cần xác nhận API xóa + AnnouncementForm.js prefill
                        đúng owner_type - việc riêng, chưa làm ở đây) - không để nút giả bấm vô tác
                        dụng đánh lừa người dùng. */}
                </div>
            </div>
            <span className="font-bold text-base text-slate-800 leading-snug">{item.title}</span>
            {!!item.content && (
                <p className="text-sm text-color-300 leading-relaxed max-lines-2">{item.content}</p>
            )}
            <div className="flex flex-wrap items-center justify-between gap-2">
                {totalCount > 0 && (
                    <span className="flex items-center gap-1.5 text-xs text-slate-400">
                        <BsEyeFill size={12}/>
                        {t('read count')}: {readCount} / {totalCount} {t('students')} ({pct}%)
                    </span>
                )}
                <span className="text-xs font-semibold text-slate-600">{t('posted by')}: {publisher}</span>
            </div>
        </div>
    )
}

const CourseNotificationsPanel = ({owner_id, owner_type, orgTitle, orgStudentCount}) => {
    const t = useTranslate()
    const [activeTab, setActiveTab] = useState('all')
    const [classPickerOpen, setClassPickerOpen] = useState(false)
    const [pickedClassForAnnouncement, setPickedClassForAnnouncement] = useState(null)
    const [coursePickerOpen, setCoursePickerOpen] = useState(false)
    const [pickedCourseForAnnouncement, setPickedCourseForAnnouncement] = useState(null)
    const {organization} = useContext(WorkspaceContext)
    const groups = _.get(organization, 'groups', [])

    // Tái dùng NGUYÊN useAnnouncementForm (đã dùng ở WorkspaceOrganizationAnnouncements.js) - không
    // tự chế modal/API riêng. 3 owner_type áp dụng ở dashboard này - xem comment đầu file.
    const {
        modal: orgAnnouncementModal,
        handleAction: handleOrgAnnouncementAction,
        refresh_token: orgAnnouncementRefreshToken,
    } = useAnnouncementForm({owner_type: 'organization', ownerItem: organization, groups})

    const {
        modal: courseAnnouncementModal,
        handleAction: handleCourseAnnouncementAction,
        refresh_token: courseAnnouncementRefreshToken,
    } = useAnnouncementForm({owner_type: 'course', ownerItem: pickedCourseForAnnouncement})

    const {
        modal: classAnnouncementModal,
        handleAction: handleClassAnnouncementAction,
        refresh_token: classAnnouncementRefreshToken,
    } = useAnnouncementForm({owner_type: 'course_class', ownerItem: pickedClassForAnnouncement})

    const handleCreateClick = () => {
        // Tab đang xem quyết định scope tạo mới. Mặc định (tab "Tất cả" - tab landing đầu tiên) VÀ
        // tab "Theo lớp cụ thể" đều mở bước chọn lớp trước - bạn báo hầu hết nhu cầu thực tế ở khối
        // này là phát thông báo TRONG 1 lớp học cụ thể, không phải toàn trung tâm, nên "Tất cả" không
        // nên mặc định rơi vào tạo thông báo chung. Chỉ khi chủ động bấm đúng tab "Thông báo chung"/
        // "Theo khóa" mới tạo thẳng owner_type='organization'/'course' tương ứng.
        if (activeTab === 'general') {
            handleOrgAnnouncementAction(CRUD.CREATE)
        } else if (activeTab === 'course') {
            setCoursePickerOpen(true)
        } else {
            setClassPickerOpen(true)
        }
    }

    // useAsync (modules/asyncCache/useAsync.js) chỉ THẬT SỰ dispatch lại action khi TOÀN BỘ phần tử
    // trong `deps` đều "có giá trị" (khác null/undefined) - đúng cho case 1 dep bắt buộc (vd chờ 1 id
    // tải xong mới fetch), nhưng SAI Ở ĐÂY vì mình gộp 3 refresh_token ĐỘC LẬP (từ 3 useAnnouncementForm
    // riêng - organization/course/course_class) vào chung 1 mảng deps: mới tạo announcement Ở 1 SCOPE
    // (vd course) thì CHỈ token của scope đó đổi, 2 token còn lại vẫn còn nguyên undefined (chưa ai
    // tạo announcement ở scope đó bao giờ) → deps.every(...) luôn false → list KHÔNG tự tải lại, phải
    // F5 mới thấy (bạn báo). Fix bằng cách ép cả 3 token về giá trị mặc định 0 (khác null/undefined)
    // NGAY TỪ ĐẦU thay vì sửa gate chung của useAsync.js (dùng ở rất nhiều nơi khác, không tự ý đổi
    // hành vi 1 hook dùng chung chỉ để vá 1 chỗ gọi sai cách).
    const safeOrgToken = orgAnnouncementRefreshToken ?? 0
    const safeCourseToken = courseAnnouncementRefreshToken ?? 0
    const safeClassToken = classAnnouncementRefreshToken ?? 0

    // status: 1 - CHỈ lấy thông báo chưa hết hiệu lực (chưa/sắp/đang diễn ra), khớp
    // Announcement::effectiveEndSql() ở backend (time_close nếu có, không thì hết ngày time_open/
    // created_at + 1 ngày) - trước đây dashboard này KHÔNG lọc gì, hiện đủ mọi thông báo kể cả đã hết
    // hạn từ lâu (bạn báo lỗi). 2 API "by owner" TRƯỚC ĐÂY chưa hỗ trợ status - đã thêm ở
    // AnnouncementController (kristo repo, getsByClassesOwner/getsByCoursesOwner) để dùng chung ĐÚNG
    // 1 định nghĩa "hết hiệu lực" với announcement_gets_Api (owner_type=organization) thay vì tự chế
    // filter riêng ở đây.
    const {response: generalResponse, isLoading: isGeneralLoading} = useAsync({
        apiInfo: announcement_gets_Api,
        values: {owner_type, owner_id, status: 1},
        deps: [owner_type, owner_id, safeOrgToken, safeCourseToken, safeClassToken],
    })
    const {response: courseResponse, isLoading: isCourseLoading} = useAsync({
        apiInfo: announcement_getsByCoursesOwner_Api,
        query: bindQueryParams([{type: owner_type}, {id: owner_id}]),
        values: {status: 1},
        deps: [owner_type, owner_id, safeOrgToken, safeCourseToken, safeClassToken],
    })
    const {response: classResponse, isLoading: isClassLoading} = useAsync({
        apiInfo: announcement_getsByClassesOwner_Api,
        query: bindQueryParams([{type: owner_type}, {id: owner_id}]),
        values: {status: 1},
        deps: [owner_type, owner_id, safeOrgToken, safeCourseToken, safeClassToken],
    })

    const items = useMemo(() => {
        // owner_type==='organization': chỉ giữ announcement THẬT SỰ toàn tổ chức, loại bỏ
        // announcement riêng của group/department con (không thuộc phạm vi Đào tạo & Huấn luyện) -
        // xem ghi chú đầu file.
        const general = (getResponseItems(generalResponse) ?? []).filter((n) => n.owner_type === 'organization')
        const byCourse = getResponseItems(courseResponse) ?? []
        const byClass = getResponseItems(classResponse) ?? []
        return _.orderBy([...general, ...byCourse, ...byClass], (n) => n.created ? dayjs(n.created).valueOf() : 0, 'desc')
    }, [generalResponse, courseResponse, classResponse])

    const visibleItems = useMemo(() => {
        const tab = FILTER_TABS.find((tb) => tb.key === activeTab)
        return tab?.ownerType ? items.filter((n) => n.owner_type === tab.ownerType) : items
    }, [items, activeTab])

    const isLoading = isGeneralLoading || isCourseLoading || isClassLoading

    return (
        <>
        <div className="p-4 md:p-6 background border border-color-50 rounded-3xl shadow-sm flex flex-col gap-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="flex flex-col gap-0.5">
                    <span className="flex items-center gap-1.5 text-xs font-bold text-orange-600 uppercase tracking-wide">
                        <BsMegaphoneFill size={12}/>
                        {t('communications & notifications administration')}
                    </span>
                    <h3 className="font-bold text-lg md:text-xl text-slate-800 tracking-wide">
                        {t('system & class notifications management')}
                    </h3>
                </div>
                <button
                    type="button"
                    onClick={handleCreateClick}
                    style={{background: '#1c4a75'}}
                    className="px-4 py-2.5 flex items-center justify-center gap-1.5 rounded-xl text-sm font-bold text-white shrink-0 hover:opacity-90 transition-opacity cursor-pointer border-0">
                    <BsSendFill size={13}/>
                    {t('create & send notification')}
                </button>
            </div>

            <div className="border-t border-color-100"/>

            <div className="flex flex-wrap items-center justify-between gap-2">
                {/* overflow-x-auto - 4 tab (Tất cả/Thông báo chung/Theo khóa/Theo lớp cụ thể) tràn ngang
                    trên mobile hẹp (375px) - đã thử flex-wrap (xuống dòng 2) nhưng bạn không muốn xuống
                    dòng, đổi qua cuộn ngang trong chính hàng tab (khớp cách đã sửa cho toggle Hôm nay/
                    Dạng List/Dạng Calendar ở CourseSchedulePanel.js - đồng nhất 1 kiểu xử lý overflow
                    hàng ngang trong cả Tổng quan). min-w-0 để div này co được trong flex-wrap cha,
                    không ép cả hàng rộng ra chỉ vì nội dung bên trong dài. */}
                {/* scrollbar-none (tailwind_custom.css, đã có sẵn) - overflow-x-auto tràn lố vài px
                    do bo tròn subpixel làm lộ 1 vệt scrollbar dọc mảnh ngay sau tab cuối (bạn báo
                    lỗi), ẩn thanh cuộn khỏi mắt mà vẫn cuộn ngang được bình thường. */}
                <div className="flex flex-nowrap items-center gap-1 min-w-0 max-w-full overflow-x-auto scrollbar-none">
                    {FILTER_TABS.map((tab) => {
                        const count = tab.ownerType ? items.filter((n) => n.owner_type === tab.ownerType).length : items.length
                        const isActive = activeTab === tab.key
                        return (
                            <button
                                key={tab.key}
                                type="button"
                                onClick={() => setActiveTab(tab.key)}
                                className={classNames(
                                    'px-2.5 py-1.5 flex items-center gap-1.5 text-sm font-semibold cursor-pointer bg-transparent border-0 border-b-2 -mb-px whitespace-nowrap flex-shrink-0 transition-colors duration-200',
                                    isActive ? 'text-slate-800 border-slate-800' : 'text-slate-400 border-transparent hover:text-slate-600',
                                )}>
                                {tab.key === 'class' && <BsPeopleFill size={12}/>}
                                {tab.key === 'course' && <FaChalkboard size={12}/>}
                                {tab.key === 'general' && <BsGlobeAmericas size={12}/>}
                                {t(tab.label)}{tab.key === 'all' ? ` (${count})` : ''}
                            </button>
                        )
                    })}
                </div>

            </div>

            {isLoading && !items.length ? (
                <div className="flex flex-col gap-4">
                    {[1, 2].map((i) => (
                        <div key={i} className="flex flex-col gap-2 animate-pulse">
                            <div style={{height: 20, width: 220}} className="background-200 rounded-full"/>
                            <div style={{height: 16, width: '70%'}} className="background-200 rounded"/>
                            <div style={{height: 12, width: '90%'}} className="background-200 rounded"/>
                        </div>
                    ))}
                </div>
            ) : !visibleItems.length ? (
                <div className="py-8 text-center text-sm text-slate-300 italic">
                    {t('no notifications yet')}
                </div>
            ) : (
                <div className="flex flex-col divide-y divide-slate-100">
                    {visibleItems.map((item) => (
                        <NotificationRow key={item.id} item={item} orgTitle={orgTitle} orgStudentCount={orgStudentCount} t={t}/>
                    ))}
                </div>
            )}
        </div>
        {orgAnnouncementModal}
        {courseAnnouncementModal}
        {classAnnouncementModal}
        <Modal
            footer={null}
            open={coursePickerOpen}
            onCancel={() => setCoursePickerOpen(false)}
            destroyOnHidden={true}
            className="custom-modal"
            title={t('choose course to notify')}>
            <div className="flex flex-col gap-2">
                <span className="text-xs text-color-300 italic">
                    {t('choose course to notify description')}
                </span>
                <CourseSelector
                    name="course_for_announcement"
                    owner_id={owner_id}
                    owner_type={owner_type}
                    onChange={(e) => {
                        const selected = e?.target?.value
                        if (!selected) return
                        setPickedCourseForAnnouncement(selected)
                        setCoursePickerOpen(false)
                        handleCourseAnnouncementAction(CRUD.CREATE)
                    }}
                />
            </div>
        </Modal>
        <Modal
            footer={null}
            open={classPickerOpen}
            onCancel={() => setClassPickerOpen(false)}
            destroyOnHidden={true}
            className="custom-modal"
            title={t('choose class to notify')}>
            <div className="flex flex-col gap-2">
                <span className="text-xs text-color-300 italic">
                    {t('choose class to notify description')}
                </span>
                <CourseClassSelector
                    name="course_class_for_announcement"
                    owner_id={owner_id}
                    owner_type={owner_type}
                    onChange={(e) => {
                        const selected = e?.target?.value
                        if (!selected) return
                        setPickedClassForAnnouncement(selected)
                        setClassPickerOpen(false)
                        handleClassAnnouncementAction(CRUD.CREATE)
                    }}
                />
            </div>
        </Modal>
        </>
    )
}

export default CourseNotificationsPanel
